import { useMemo, type ReactNode, type RefObject } from 'react';
import { paigeShortcutGroups } from '@paige/capturepoint';
import { Dialog } from '@paige/ui';
import './help.css';

/** Keys pressed together sit side by side; alternatives are separated by a slash (the F1 sheet's own reading). */
function Keys({ combo }: { combo: string }): ReactNode {
  return (
    <span className="help-keys">
      {combo.split(' / ').map((alternative, index) => (
        <span key={alternative} className="help-keys__combo">
          {index > 0 && <span aria-label="or">/</span>}
          {alternative.split('+').map((part) => (
            <kbd key={part}>{part}</kbd>
          ))}
        </span>
      ))}
    </span>
  );
}

/**
 * The F1 sheet outside a job (Help's Keyboard shortcuts, or F1 on the dashboard): the review screen's keys,
 * exactly as the workbench's own sheet lists them in a browser (`paigeShortcutGroups`), in Paige's dialog. The
 * list scrolls, so it takes the keyboard's focus too (axe, scrollable-region-focusable).
 */
export function ShortcutsSheet({
  onClose,
  returnFocusTo,
}: {
  onClose: () => void;
  returnFocusTo: RefObject<HTMLElement | null>;
}): ReactNode {
  const groups = useMemo(() => paigeShortcutGroups(), []);
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      title="Keyboard shortcuts"
      description="On a job’s review screen. Press F1 there to open this sheet."
      returnFocusTo={returnFocusTo}
    >
      <div className="help-shortcuts" tabIndex={0} role="group" aria-label="Every key">
        {groups.map((group) => (
          <section key={group.title} aria-label={group.title}>
            <h3>{group.title}</h3>
            <dl>
              {group.rows.map((row) => (
                <div key={`${row.keys}:${row.action}`} className="help-shortcut">
                  <dt>
                    {row.action}
                    {row.note && <small>{row.note}</small>}
                  </dt>
                  <dd>
                    <Keys combo={row.keys} />
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
